/**
 * Quiet platform context strip for Create Experiment — matches InstallScriptPanel
 * picker UX (muted header, full primary set, More / Other, Wrong platform?).
 */

import { useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { HugeiconsIcon } from "@hugeicons/react"
import { Loading01Icon } from "@hugeicons/core-free-icons"
import { PlatformGlyph } from "@/components/onboarding/PlatformGlyph"
import {
  getPlatformGuide,
  MORE_SELECTABLE_PLATFORMS,
  PRIMARY_SELECTABLE_PLATFORMS,
  platformGuides,
} from "@/data/platform-guides"
import type { PlatformId } from "@/lib/types/platform"
import { cn } from "@/lib/utils"

type ExperimentPlatformStripProps = {
  domain?: string | null
  platform: PlatformId | null
  isDetecting?: boolean
  useCaseLabel?: string | null
  hint?: string | null
  onSelect: (platform: PlatformId) => void
  className?: string
}

export function ExperimentPlatformStrip({
  domain,
  platform,
  isDetecting = false,
  useCaseLabel,
  hint,
  onSelect,
  className,
}: ExperimentPlatformStripProps) {
  const resolved = platform && platform !== "generic" ? platform : null
  const [showPicker, setShowPicker] = useState(!resolved)
  const [showMore, setShowMore] = useState(false)

  // Collapse when detection upgrades; keep open when still unknown.
  useEffect(() => {
    if (resolved) setShowPicker(false)
    else setShowPicker(true)
  }, [resolved])

  const pickerOpen = showPicker || !resolved

  const pickerIds = useMemo(() => {
    if (showMore) return [...PRIMARY_SELECTABLE_PLATFORMS, ...MORE_SELECTABLE_PLATFORMS]
    return PRIMARY_SELECTABLE_PLATFORMS
  }, [showMore])

  const guideName = resolved ? getPlatformGuide(resolved).name : null
  const host = domain?.replace(/^https?:\/\//, "").replace(/\/$/, "") || "your site"

  const badge = isDetecting && !resolved
    ? "Detecting"
    : resolved
      ? "Detected"
      : null

  return (
    <div
      className={cn(
        "rounded-lg border border-border/30 bg-muted/15 px-3.5 py-3 space-y-2",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap text-[12px]">
            {resolved && <PlatformGlyph platform={resolved} size={14} className="size-3.5" />}
            {isDetecting && !resolved ? (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <HugeiconsIcon
                  icon={Loading01Icon}
                  size={12}
                  className="animate-spin text-muted-foreground"
                />
                Detecting site type for {host}…
              </span>
            ) : resolved ? (
              <span>
                <span className="text-muted-foreground">Editing on </span>
                <span className="font-medium text-foreground">{guideName}</span>
              </span>
            ) : (
              <span className="font-medium text-foreground">
                Which platform is {host} built on?
              </span>
            )}
            {badge && (
              <span
                className={cn(
                  "text-[11px] font-medium rounded-full border px-1.5 py-0.5",
                  isDetecting && !resolved
                    ? "border-border/30 text-muted-foreground bg-muted/40"
                    : "border-success/30 text-success dark:text-success bg-success/10",
                )}
              >
                {badge}
              </span>
            )}
            {useCaseLabel && (
              <>
                <span className="h-3 w-px bg-border/60" aria-hidden />
                <span>
                  <span className="text-muted-foreground">Use case </span>
                  <span className="font-medium text-foreground">{useCaseLabel}</span>
                </span>
              </>
            )}
          </div>
          {!resolved && (
            <p className="text-[11px] text-muted-foreground leading-snug">
              Pick your builder for editor tips, or continue with Other.
            </p>
          )}
        </div>

        {resolved && (
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            className="text-[11px] text-muted-foreground hover:text-foreground shrink-0 underline-offset-2 hover:underline"
          >
            {showPicker ? "Hide" : "Wrong platform?"}
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {pickerOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ overflow: "hidden" }}
          >
            <div className="pt-0.5 flex flex-wrap gap-1.5">
              {pickerIds.map((id) => {
                const item = platformGuides.find((g) => g.id === id)
                if (!item) return null
                const active = resolved === id
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      onSelect(id)
                      setShowPicker(false)
                    }}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                      active
                        ? "border-foreground/80 bg-muted/40 text-foreground"
                        : "border-border/50 bg-card text-muted-foreground hover:border-border/80 hover:text-foreground",
                    )}
                  >
                    <PlatformGlyph platform={id} size={14} className="h-3.5 w-3.5" />
                    {item.name}
                  </button>
                )
              })}
              {!showMore && (
                <button
                  type="button"
                  onClick={() => setShowMore(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border/50 bg-card px-2 py-1 text-[11px] font-medium text-muted-foreground hover:border-border/80 hover:text-foreground transition-colors"
                >
                  More platforms
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  onSelect("generic")
                  setShowPicker(true)
                }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                  !resolved
                    ? "border-foreground/80 bg-muted/40 text-foreground"
                    : "border-border/50 bg-card text-muted-foreground hover:border-border/80 hover:text-foreground",
                )}
              >
                Other
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {hint && (
        <p className="text-[12px] leading-snug text-foreground/85">{hint}</p>
      )}
    </div>
  )
}
